import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, FileSpreadsheet, FileDown, Save, Network, Download,
  ChevronDown, ShieldCheck, Cpu, Sparkles, Mail, ClipboardList, CheckCircle2, MessageSquare, Truck,
} from "lucide-react";
import HardwareSpecsCard from "@/components/HardwareSpecsCard";
import BomRecommendations from "@/components/BomRecommendations";
import EmailQuoteToTeam from "@/components/EmailQuoteToTeam";
import { WAF_COUNTRY_AMS } from "@/data/waf-region";

const fmt = (v, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(v);

const FRESHNESS_STYLES = {
  fresh:   { dot: "bg-green-500",  label: "À jour",       chip: "bg-green-50 text-green-800 border-green-200" },
  review:  { dot: "bg-amber-500",  label: "À vérifier",   chip: "bg-amber-50 text-amber-900 border-amber-300" },
  stale:   { dot: "bg-red-500",    label: "Dépassée",     chip: "bg-red-50 text-red-800 border-red-300" },
  unknown: { dot: "bg-zinc-400",   label: "Date inconnue", chip: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};

function formatFrenchDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return null; }
}

// Site-role badge shown next to FortiGate SKUs in the BOM·BOQ table. The
// engine prefixes hardware descriptions with "[HQ]", "[Branch]" or "[Site]"
// — we extract that and render it as a coloured pill (matching the
// HardwareSpecsCard role band) so partners can scan a 30+ line BOM and
// instantly tell which line belongs to which site.
const ROLE_BADGE_THEME = {
  HQ:     { label: "Siège",  cls: "bg-[#D42B1E] text-white" },
  Branch: { label: "Agence", cls: "bg-zinc-800 text-white" },
  Site:   { label: "Site",   cls: "bg-amber-600 text-white" },
};

function extractRole(desc) {
  if (!desc || typeof desc !== "string") return null;
  const m = desc.match(/^\s*\[(HQ|Branch|Site)\]\s*/i);
  if (!m) return null;
  // Normalise the captured key so "branch" → "Branch".
  const key = m[1].toUpperCase() === "HQ"
    ? "HQ"
    : m[1].slice(0, 1).toUpperCase() + m[1].slice(1).toLowerCase();
  return { theme: ROLE_BADGE_THEME[key] || null, cleanDesc: desc.replace(m[0], "") };
}

function PricelistSourceBanner({ src }) {
  if (!src) return null;
  const status = src.freshness_status || "unknown";
  const S = FRESHNESS_STYLES[status] || FRESHNESS_STYLES.unknown;
  const eff = formatFrenchDate(src.effective_date);
  return (
    <div
      className="px-5 py-2.5 bg-zinc-50/70 border-b border-zinc-100 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]"
      data-testid="bom-pricelist-source-banner"
    >
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border font-mono font-semibold ${S.chip}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${S.dot}`} />
        {S.label}
        {src.age_days != null && <span className="opacity-70">· {src.age_days}j</span>}
      </span>
      <span className="text-zinc-700">
        BOM calculée depuis&nbsp;
        <span className="font-mono text-zinc-900" data-testid="bom-pricelist-filename">
          {src.filename || "Liste de prix Fortinet"}
        </span>
        {eff && (
          <>
            &nbsp;· date d'effet&nbsp;
            <span className="font-semibold text-zinc-900">{eff}</span>
          </>
        )}
      </span>
      {src.row_count != null && (
        <span className="text-zinc-500 font-mono">
          {src.row_count.toLocaleString("fr-FR")} réf.
        </span>
      )}
      {src.user_overrides_applied && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white font-mono uppercase tracking-wide" data-testid="bom-pricelist-overrides-badge">
          Discount Partenaire
        </span>
      )}
    </div>
  );
}

export default function ResultsView({
  result, onSave, saving, onAddAddon, pendingAddons, customerInfo,
  savedQuoteId, reviewRequested, assignedSe, onRequestReview, onOpenChatRoom, onAssignSE,
  highlightBom = false, quote = null,
}) {
  const { user } = useAuth();
  const [diagramSvg, setDiagramSvg] = useState("");
  const [loadingDiagram, setLoadingDiagram] = useState(false);
  // "commercial" shows all price columns and totals; "technical" hides Prix
  // public + Remise + Prix net/unit + Total so the table can be shared with
  // an end-customer without revealing partner margin or net pricing.
  const [tableView, setTableView] = useState("commercial");
  const showPrices = tableView === "commercial";

  useEffect(() => {
    if (!result) return;
    if (result.inputs?.renewal_mode || result.inputs?.solutions_only_mode) {
      setDiagramSvg("");
      return;
    }
    setLoadingDiagram(true);
    api.post("/export/architecture", result, { responseType: "text", transformResponse: [(d) => d] })
      .then((r) => setDiagramSvg(r.data))
      .catch(() => setDiagramSvg(""))
      .finally(() => setLoadingDiagram(false));
  }, [result]);

  if (!result) return null;

  const downloadSvg = () => {
    if (!diagramSvg) return;
    const blob = new Blob([diagramSvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fortinet-architecture-${result.inputs.use_case.toLowerCase().replace(/\s+/g, "-")}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Schéma d'architecture téléchargé");
  };

  // Build the export-header metadata block sent to /api/export/{pdf,excel}.
  // Surfaces: end-customer identity, partner identity + level, BOM author,
  // and the on-zone Fortinet reviewer (the assigned SE, if a review was at
  // least requested on this quote).
  const buildExportMeta = () => {
    const meta = {};
    if (customerInfo) meta.customer_info = customerInfo;
    if (user?.company) meta.partner_name = user.company;
    if (user?.partner_level) meta.partner_level = user.partner_level;
    if (user?.full_name) meta.generated_by_name = user.full_name;
    if (user?.email) meta.generated_by_email = user.email;
    // Reviewer = assigned SE on the quote — only stamped on the export when
    // a review actually took place (review requested or resolved).
    const se = assignedSe || quote?.assigned_se || null;
    const status = quote?.review_status || (reviewRequested ? "requested" : null);
    if (se && status) {
      if (se.name) meta.reviewer_name = se.name;
      if (se.email) meta.reviewer_email = se.email;
      if (quote?.review_resolved_at) {
        meta.reviewed_at = new Date(quote.review_resolved_at).toLocaleDateString("fr-FR");
      }
    }
    return meta;
  };

  const doExport = async (kind) => {
    try {
      const meta = buildExportMeta();
      const payload = Object.keys(meta).length > 0 ? { result, meta } : result;
      const r = await api.post(`/export/${kind}`, payload, { responseType: "blob" });
      const mime = {
        pdf: "application/pdf",
        excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        csv: "text/csv",
      }[kind];
      const ext = kind === "excel" ? "xlsx" : kind;
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fortinet-bom.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${ext.toUpperCase()} réussi`);
    } catch (e) {
      toast.error("Échec de l'export");
    }
  };

  return (
    <div className="space-y-4" data-testid="results-view">
      {/* Compact action toolbar — exports + save kept on top so partners
          always see the primary CTAs even before scrolling through the BOM. */}
      <div className="border border-zinc-200 bg-white rounded-sm px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="results-toolbar">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[#D42B1E]" />
          <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-500">
            Devis généré · {result.bom?.length || 0} ligne{(result.bom?.length || 0) > 1 ? "s" : ""}
          </span>
          {result.total_cost != null && (
            <span className="text-sm font-mono font-semibold text-zinc-900 ml-2">
              {fmt(result.total_cost, result.currency)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onSave && !savedQuoteId && (
            <Button size="sm" onClick={onSave} disabled={saving} className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm" data-testid="save-quote-button">
              <Save className="h-3.5 w-3.5 mr-1.5" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          )}
          {savedQuoteId && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="quote-saved-badge">
              <CheckCircle2 className="h-3 w-3" /> Devis enregistré
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => doExport("pdf")} className="rounded-sm border-zinc-200" data-testid="export-pdf-button">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => doExport("excel")} className="rounded-sm border-zinc-200" data-testid="export-excel-button">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => doExport("csv")} className="rounded-sm border-zinc-200" data-testid="export-csv-button">
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* BOM-driven complementary solutions — moved below to the unified Recommendations panel */}

      {/* BOM + BOQ — table unifiée. Always visible on top: this is the
          primary deliverable and what partners come to the page for. When
          `highlightBom` is set (e.g. on the Mes Devis page), the block gets
          a red accent ring + LIVRABLE PRINCIPAL pill so it visually pops
          above the collapsed secondary sections. */}
      <div
        className={`bg-white rounded-sm overflow-hidden ${
          highlightBom
            ? "border-2 border-[#D42B1E]/60 ring-4 ring-[#D42B1E]/10 shadow-lg shadow-[#D42B1E]/10"
            : "border border-zinc-200"
        }`}
        data-testid="bom-boq-block"
      >
        {highlightBom && (
          <div className="bg-[#D42B1E] text-white px-5 py-1.5 flex items-center justify-between" data-testid="bom-boq-featured-banner">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] inline-flex items-center gap-1.5">
              <ClipboardList className="h-3 w-3" />
              Livrable principal
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wide opacity-80">
              BOM · BOQ · Total
            </span>
          </div>
        )}
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
            Bill of Materials & Quantities (BOM · BOQ)
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            {/* View toggle: commercial reveals prices, technical hides them */}
            <div
              className="inline-flex items-center rounded-sm border border-zinc-200 overflow-hidden text-[11px]"
              data-testid="table-view-toggle"
              role="group"
              aria-label="Mode d'affichage"
            >
              <button
                type="button"
                onClick={() => setTableView("commercial")}
                className={`px-2.5 py-1 font-semibold tracking-wide uppercase transition-colors ${
                  tableView === "commercial"
                    ? "bg-[#D42B1E] text-white"
                    : "bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="table-view-commercial"
                aria-pressed={tableView === "commercial"}
              >
                Vue commerciale
              </button>
              <button
                type="button"
                onClick={() => setTableView("technical")}
                className={`px-2.5 py-1 font-semibold tracking-wide uppercase transition-colors border-l border-zinc-200 ${
                  tableView === "technical"
                    ? "bg-[#D42B1E] text-white"
                    : "bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid="table-view-technical"
                aria-pressed={tableView === "technical"}
              >
                Vue technique
              </button>
            </div>
            {showPrices && result.partner_level && (
              <span className="text-[11px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-sm border bg-[#D42B1E]/10 border-[#D42B1E]/30 text-[#991D14]" data-testid="boq-partner-badge">
                Partenaire {result.partner_level} · HW -{Math.round((result.partner_discounts?.hw || 0) * 100)}% · SW -{Math.round((result.partner_discounts?.sw || 0) * 100)}%
              </span>
            )}
            <span className="text-xs font-mono text-zinc-500">
              {result.bom.length} éléments{showPrices ? ` · ${result.currency}` : ""}
            </span>
          </div>
        </div>
        {showPrices && result.pricelist_source && (
          <PricelistSourceBanner src={result.pricelist_source} />
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bom-boq-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Référence</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Produit</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Type</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Description</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Catégorie</th>
                {showPrices && result.partner_level && (
                  <>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Prix public</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Remise</th>
                  </>
                )}
                {showPrices && (
                  <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">{result.partner_level ? "Prix net" : "Prix unitaire"}</th>
                )}
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Qté</th>
                {showPrices && (
                  <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Total</th>
                )}
              </tr>
            </thead>
            <tbody>
              {result.bom.map((bomRow, idx) => {
                // BOM and BOQ are kept in lockstep by the engine — match by index,
                // fall back to SKU lookup if the lengths happen to differ.
                const boqRow =
                  result.boq[idx] && result.boq[idx].sku === bomRow.sku
                    ? result.boq[idx]
                    : result.boq.find((r) => r.sku === bomRow.sku) || {};
                const roleInfo = extractRole(bomRow.description);
                return (
                  <tr
                    key={idx}
                    className={`border-b border-zinc-100 hover:bg-zinc-50 ${idx % 2 ? "bg-zinc-50/40" : ""}`}
                    data-testid={`bom-boq-row-${idx}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{bomRow.sku}</td>
                    <td className="px-4 py-2.5 text-zinc-900 font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {roleInfo?.theme && (
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide whitespace-nowrap ${roleInfo.theme.cls}`}
                            data-testid={`bom-role-badge-${idx}`}
                          >
                            {roleInfo.theme.label}
                          </span>
                        )}
                        <span>{bomRow.name}</span>
                        {bomRow.service_tag === "mandatory" && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-[#D42B1E] text-white rounded-sm font-medium uppercase tracking-wide whitespace-nowrap" data-testid={`bom-service-tag-mandatory-${idx}`}>
                            Service obligatoire
                          </span>
                        )}
                        {bomRow.service_tag === "optional" && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-zinc-700 text-white rounded-sm font-medium uppercase tracking-wide whitespace-nowrap" data-testid={`bom-service-tag-optional-${idx}`}>
                            Professional Services
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-sm border ${
                        bomRow.type === "Hardware Bundle"
                          ? "bg-[#D42B1E]/10 border-[#D42B1E]/30 text-[#991D14]"
                          : bomRow.type === "Hardware"
                          ? "bg-zinc-900/5 border-zinc-300 text-zinc-800"
                          : bomRow.type === "License"
                          ? "bg-amber-50 border-amber-200 text-amber-800"
                          : bomRow.type === "Shipping"
                          ? "bg-purple-50 border-purple-200 text-purple-800"
                          : "bg-blue-50 border-blue-200 text-blue-800"
                      }`} data-testid={`bom-type-${idx}`}>
                        {bomRow.type || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600 text-xs">
                      {roleInfo ? roleInfo.cleanDesc : bomRow.description}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs">{bomRow.category}</td>
                    {result.partner_level && (
                      <>
                        <td className="px-4 py-2.5 font-mono text-right text-zinc-500 line-through" data-testid={`boq-list-${idx}`}>
                          {fmt(boqRow.list_price || boqRow.unit_price || 0, result.currency)}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-right" data-testid={`boq-discount-${idx}`}>
                          {boqRow.discount_pct ? (
                            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-green-50 text-green-800 border border-green-200">
                              -{Math.round(boqRow.discount_pct * 100)}%
                            </span>
                          ) : "—"}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2.5 font-mono text-right text-zinc-800">{fmt(boqRow.unit_price || 0, result.currency)}</td>
                    <td className="px-4 py-2.5 font-mono text-right text-zinc-900">{bomRow.quantity}</td>
                    <td className="px-4 py-2.5 font-mono text-right text-zinc-900 font-semibold">{fmt(boqRow.total_price || 0, result.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {showPrices && result.partner_level && result.total_savings > 0 && (() => {
                const gross = (result.total_cost || 0) + (result.total_savings || 0);
                const pct = gross > 0 ? (result.total_savings / gross) * 100 : 0;
                return (
                  <tr className="bg-green-50 border-t border-green-200">
                    <td colSpan={result.partner_level ? 8 : 6} className="px-4 py-2.5 text-right text-xs font-semibold text-green-800">
                      Économies partenaire ({result.partner_level})
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-green-700 text-white font-mono text-[10px] tabular-nums" data-testid="boq-savings-percent">
                        −{pct.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-right text-green-800 font-semibold" data-testid="boq-total-savings">
                      −{fmt(result.total_savings, result.currency)}
                    </td>
                  </tr>
                );
              })()}
              {showPrices && (
                <tr className="bg-[#D42B1E] text-white">
                  <td colSpan={result.partner_level ? 8 : 6} className="px-4 py-3 text-left text-[11px] font-semibold tracking-[0.18em] uppercase">
                    Coût total du projet {result.partner_level ? "(net partenaire)" : ""}
                  </td>
                  <td className="px-4 py-3 font-mono text-right text-lg font-semibold" data-testid="boq-total-cost">{fmt(result.total_cost, result.currency)}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {/* Revue Fortinet — CTA prominently placed right after the BOM table.
          Both « Demander une revue » and « Salon Fortinet » work immediately
          (the partner doesn't have to save first — the parent component
          auto-saves silently if needed). */}
      <ReviewRequestCard
        savedQuoteId={savedQuoteId}
        reviewRequested={reviewRequested}
        assignedSe={assignedSe}
        country={customerInfo?.country || ""}
        onRequestReview={onRequestReview}
        onOpenChatRoom={onOpenChatRoom}
        onAssignSE={onAssignSE}
      />

      {/* === COLLAPSIBLE SECTIONS BELOW =====================================
          Everything after the BOM (architecture summary, diagram, hardware
          specs, recommendations, email-to-team) is hidden behind a
          collapsible section so the page stays focused on the deliverable. */}

      <CollapsibleSection
        icon={ClipboardList}
        title="Synthèse d'architecture"
        testId="section-architecture-summary"
      >
        <p className="px-5 py-4 text-sm text-zinc-700 leading-relaxed" data-testid="architecture-summary-text">
          {result.architecture_summary}
        </p>
      </CollapsibleSection>

      {!result.inputs?.renewal_mode && !result.inputs?.solutions_only_mode && (
        <CollapsibleSection
          icon={Network}
          title={`Schéma d'architecture — ${result.inputs.use_case}`}
          testId="section-architecture-diagram"
          extraAction={
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); downloadSvg(); }}
              disabled={!diagramSvg}
              className="rounded-sm border-zinc-200"
              data-testid="download-architecture-button"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Télécharger SVG
            </Button>
          }
        >
          <div className="bg-[#FAFAFA] p-4 flex items-center justify-center min-h-[300px]">
            {loadingDiagram ? (
              <p className="text-sm text-zinc-500">Génération du schéma…</p>
            ) : diagramSvg ? (
              <div
                className="w-full max-w-[1100px] mx-auto"
                data-testid="architecture-diagram-svg"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: diagramSvg }}
              />
            ) : (
              <p className="text-sm text-zinc-500">Schéma indisponible.</p>
            )}
          </div>
        </CollapsibleSection>
      )}

      {result.hardware_specs && (
        <CollapsibleSection
          icon={Cpu}
          title="Spécifications matérielles FortiGate"
          testId="section-hardware-specs"
        >
          <HardwareSpecsCard hardwareSpecs={result.hardware_specs} embedded />
        </CollapsibleSection>
      )}

      {/* Shipping breakdown (if present) */}
      {result.shipping && (
        <CollapsibleSection
          icon={Truck}
          title={`Coût de transport — ${result.shipping.carrier_name}`}
          testId="shipping-card"
          extraAction={
            <span className="text-[10px] font-mono text-purple-700 px-2 py-0.5 rounded-sm bg-purple-50 border border-purple-200">
              Zone {result.shipping.zone}
            </span>
          }
        >
          <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-purple-50/40">
            <div>
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-purple-700">Poids réel</div>
              <div className="mt-1 font-mono text-zinc-900" data-testid="shipping-actual-kg">{result.shipping.total_actual_kg} kg</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-purple-700">Poids facturable</div>
              <div className="mt-1 font-mono text-zinc-900" data-testid="shipping-chargeable-kg">{result.shipping.total_chargeable_kg} kg</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-purple-700">Tarif</div>
              <div className="mt-1 font-mono text-zinc-900">${result.shipping.rate_per_kg.toFixed(2)}/kg</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-purple-700">Surcharge carburant</div>
              <div className="mt-1 font-mono text-zinc-900">{Math.round(result.shipping.fuel_surcharge_pct * 100)}%</div>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-purple-200/60 flex items-center justify-between text-sm bg-purple-50/40">
            <div className="text-xs text-purple-800 font-mono">
              Base ${result.shipping.base_cost.toFixed(2)} + Carburant ${result.shipping.fuel_cost.toFixed(2)} × Zone {result.shipping.zone_multiplier}
            </div>
            <div className="font-mono font-semibold text-purple-900" data-testid="shipping-total-cost">
              Total transport : ${result.shipping.total_cost.toFixed(2)}
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Recommendations — unified panel: addon cards + textual upsell */}
      <CollapsibleSection
        icon={Sparkles}
        title="Recommandations & Upsell"
        testId="section-recommendations"
        defaultOpen={(result.addon_recommendations?.length || 0) > 0}
      >
        {/* Addon recommendation cards (FortiMail, FortiWeb, FortiAuthenticator…) */}
        <BomRecommendations
          recommendations={result.addon_recommendations || []}
          onAddAddon={onAddAddon}
          securityLevel={result.inputs?.security_level}
          licenseYears={result.inputs?.license_years}
          bom={result.bom || []}
          pendingAddons={pendingAddons || []}
          embedded
        />
        {/* Textual upsell bullets from the engine */}
        {result.recommendations?.length > 0 && (
          <ul
            className="px-5 py-4 space-y-2 border-t border-zinc-100"
            data-testid="recommendations-list"
          >
            {result.recommendations.map((r, idx) => (
              <li key={idx} className="flex gap-3 text-sm text-zinc-700">
                <span className="text-[#D42B1E] font-mono">→</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleSection>

      {/* Envoi du BOM aux équipes Fortinet — repliable comme dernier bloc. */}
      <CollapsibleSection
        icon={Mail}
        title="Envoyer ce BOM aux équipes Fortinet"
        testId="section-email-fortinet"
      >
        <EmailQuoteToTeam result={result} customerInfo={customerInfo} embedded />
      </CollapsibleSection>
    </div>
  );
}


/**
 * Collapsible section — closed by default. Used for every secondary block
 * after the BOM table to keep the page short and focused.
 */
function CollapsibleSection({ icon: Icon, title, children, defaultOpen = false, testId, extraAction }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-zinc-50/50 transition-colors text-left"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-zinc-400" />}
          <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {extraAction}
          <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && <div className="border-t border-zinc-100">{children}</div>}
    </div>
  );
}

/**
 * Revue Fortinet card — placed right after the BOM table.
 * No save-first gating: the parent component auto-creates the quote
 * silently when either CTA is clicked. Two parallel paths:
 *   • « Demander une revue »   → /api/quotes/{id}/review-request
 *   • « Ouvrir Salon Fortinet » → opens the QuoteChatRoom Sheet
 */
function ReviewRequestCard({ savedQuoteId, reviewRequested, assignedSe, country, onRequestReview, onOpenChatRoom, onAssignSE }) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savingSE, setSavingSE] = useState(false);

  // SE options for the country attached to the BOM. Only « System Engineer »
  // / « Presales » roles are surfaced (Account Managers are addressed via the
  // automatic mailto in the « Envoyer aux équipes » section).
  const seOptions = (() => {
    const team = (country && WAF_COUNTRY_AMS[country]) || [];
    return team.filter((m) => /System Engineer|Presales/i.test(m.role));
  })();

  const handlePickSE = async (e) => {
    const value = e.target.value;
    if (!onAssignSE) return;
    setSavingSE(true);
    try {
      if (value === "__none__") {
        await onAssignSE(null);
      } else {
        const m = seOptions.find((x) => x.email === value);
        if (m) await onAssignSE(m);
      }
    } finally {
      setSavingSE(false);
    }
  };

  return (
    <div className="border border-[#D42B1E]/30 bg-gradient-to-br from-[#D42B1E]/5 via-white to-white rounded-sm" data-testid="review-card">
      <div className="px-5 py-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className={`h-10 w-10 rounded-sm flex items-center justify-center shrink-0 ${reviewRequested ? "bg-emerald-600 shadow-md shadow-emerald-600/30" : "bg-[#D42B1E] shadow-md shadow-[#D42B1E]/30"}`}>
            {reviewRequested ? (
              <CheckCircle2 className="h-5 w-5 text-white" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] font-semibold tracking-[0.2em] uppercase ${reviewRequested ? "text-emerald-700" : "text-[#D42B1E]"}`}>
              {reviewRequested ? "Revue demandée · Salon Fortinet ouvert" : "Revue Fortinet · Disponible"}
            </p>
            <h4 className="mt-1 text-base font-semibold text-zinc-900">
              {reviewRequested
                ? "Vos échanges avec l'équipe Fortinet continuent dans le Salon."
                : "Faites valider cette BOM par un ingénieur Fortinet"}
            </h4>
            <p className="mt-1 text-xs text-zinc-600 leading-relaxed">
              {reviewRequested
                ? "Tous les Reviewers sont notifiés par e-mail. Ouvrez le Salon Fortinet pour suivre leurs corrections directement sur ce devis."
                : "Aucune étape d'enregistrement préalable : le devis est sauvegardé automatiquement dès que vous lancez la revue ou ouvrez le Salon Fortinet."}
            </p>
          </div>
          {savedQuoteId && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm self-start" data-testid="review-card-saved-badge">
              <CheckCircle2 className="h-2.5 w-2.5" /> Devis enregistré
            </span>
          )}
        </div>

        {/* SE picker — country-aware list of System Engineers */}
        {country && seOptions.length > 0 && (
          <div className="mt-4 pt-3 border-t border-zinc-200/70" data-testid="review-se-picker">
            <label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-zinc-600 flex items-center gap-1.5">
              <Cpu className="h-3 w-3 text-[#D42B1E]" />
              System Engineer alloué · {country}
              {assignedSe && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-700 normal-case tracking-normal font-medium">
                  <CheckCircle2 className="h-2.5 w-2.5" /> {assignedSe.name}
                </span>
              )}
            </label>
            <select
              value={assignedSe?.email || "__none__"}
              onChange={handlePickSE}
              disabled={savingSE}
              className="mt-1.5 w-full sm:w-auto rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#D42B1E]"
              data-testid="review-se-select"
            >
              <option value="__none__">— Aucun SE alloué —</option>
              {seOptions.map((m) => (
                <option key={m.email} value={m.email}>
                  {m.name} · {m.role}
                </option>
              ))}
            </select>
            {assignedSe && (
              <p className="mt-1.5 text-[11px] text-zinc-500 font-mono">
                Notifié par e-mail · {assignedSe.email}
              </p>
            )}
          </div>
        )}

        {/* Action row */}
        <div className="mt-4 flex flex-col lg:flex-row gap-2">
          {!reviewRequested && (
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Message optionnel (ex. priorité urgente, contexte client…)"
              maxLength={300}
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:border-[#D42B1E]"
              data-testid="review-message-input"
            />
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            {!reviewRequested && (
              <Button
                onClick={async () => {
                  setSubmitting(true);
                  try { await onRequestReview(message.trim() || null); }
                  finally { setSubmitting(false); }
                }}
                disabled={submitting}
                className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm whitespace-nowrap"
                data-testid="review-request-btn"
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                {submitting ? "Envoi…" : "Demander la revue"}
              </Button>
            )}
            {onOpenChatRoom && (
              <Button
                onClick={onOpenChatRoom}
                variant={reviewRequested ? "default" : "outline"}
                className={`rounded-sm whitespace-nowrap ${reviewRequested ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-[#D42B1E]/40 text-[#D42B1E] hover:bg-[#D42B1E]/5"}`}
                data-testid="open-chat-btn"
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Ouvrir Salon Fortinet
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
